import { AlertTriangle, Building2, TrendingUp, Wallet } from "lucide-react";
import { useMemo } from "react";
import { Card } from "../ui";
import type { Money } from "./money";
import { CATEGORIES, daysSince, eur, eurShort, Flag, Kpi, Row, STALE_DAYS, tagMatches, type Asset, type WealthSettings } from "./shared";

export interface WealthTotals { total: number; by: Record<string, number>; liquid: number }

export function OverviewTab({ assets, totals, settings, money, inflowsError }: { assets: Asset[]; totals: WealthTotals; settings: WealthSettings; money: Money | null; inflowsError: boolean }) {
  const companies = settings?.companies || [];
  const by = (k: string) => totals.by[k] ?? 0;
  const inCompanies = assets.filter((a) => a.category === "cash" && companies.some((c) => tagMatches(a.location, c.name)));
  const inCompaniesTotal = inCompanies.reduce((s, a) => s + a.value, 0);

  const flags = useMemo(() => {
    const out: Array<{ pill: string; ok?: boolean; text: string }> = [];
    const stale = assets.filter((a) => daysSince(a.valuedOn) > STALE_DAYS);
    if (stale.length) out.push({ pill: "review", text: `${stale.length} value${stale.length === 1 ? " is" : "s are"} over a year old — refresh ${stale.length === 1 ? "it" : "them"} one at a time from the Assets tab.` });
    const noValue = assets.filter((a) => !a.value);
    if (noValue.length) out.push({ pill: "add", text: `${noValue.map((a) => a.name).join(", ")} ${noValue.length === 1 ? "has" : "have"} no value yet — enter a purchase price or an estimate so ${noValue.length === 1 ? "it counts" : "they count"}.` });
    if (totals.liquid > 0) {
      const byName: Record<string, number> = {};
      for (const a of assets) if (a.category === "investment") { const n = a.name.replace(/\s+\d.*$/, ""); byName[n] = (byName[n] || 0) + a.value; }
      for (const [name, v] of Object.entries(byName)) {
        const share = v / totals.liquid;
        if (share >= 0.4) out.push({ pill: "concentration", text: `${name} is ${eurShort(v)} — ${Math.round(share * 100)}% of your liquid wealth in one investment.` });
      }
    }
    if (!companies.length) out.push({ pill: "settings", text: "No companies listed yet — add them in Wealth settings so company cash can be tagged." });
    if (!(settings?.dividendTaxPct > 0)) out.push({ pill: "tax", text: "Set the tax on the yearly dividend in Wealth settings, so the December figure shows net." });
    if (!out.length) out.push({ pill: "ok", ok: true, text: "Nothing needs attention. Values are current and every item has a figure." });
    return out;
  }, [assets, totals, settings, companies.length]);

  const sorted = [...assets].sort((a, b) => b.value - a.value);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi label="Net worth" icon={<Wallet size={18} />} value={eurShort(totals.total)} sub={`${assets.length} items · ${eur(totals.total)}`} />
        <Kpi label="Liquid now" value={eurShort(totals.liquid)} sub={`cash ${eurShort(by("cash"))} · investments ${eurShort(by("investment"))}`} />
        <Kpi label="Property" value={eurShort(by("property"))} sub={`${assets.filter((a) => a.category === "property").length} items · ${Math.round((by("property") / (totals.total || 1)) * 100)}% of net worth`} />
        <Kpi
          label="Coming in, next 12 months"
          icon={<TrendingUp size={18} />}
          value={money ? eurShort(money.contracted12 + money.scheduled12 + money.pipeline12) : inflowsError ? "—" : "…"}
          sub={money ? `contracted ${eurShort(money.contracted12)} · scheduled ${eurShort(money.scheduled12)} · pipeline ${eurShort(money.pipeline12)}` : inflowsError ? "CRM not available right now" : "reading the CRM"}
        />
        <Kpi label="Sitting in the companies" value={eurShort(inCompaniesTotal)} sub={companies.length ? `${inCompanies.length} account${inCompanies.length === 1 ? "" : "s"} · counted in net worth` : "list the companies in Wealth settings"} />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card title="What the wealth is made of" icon={<Building2 size={16} />}>
          {sorted.slice(0, 8).map((a) => (
            <Row key={a.id} label={<span title={a.name}>{a.name}</span>} bar={{ pct: Math.max(1, (a.value / (totals.total || 1)) * 100) }} value={eurShort(a.value)} />
          ))}
          {assets.length > 8 && <p className="muted mt-1.5 text-xs">+ {assets.length - 8} smaller items — see Assets</p>}
          <div className="mt-2 border-t border-[var(--line)] pt-2">
            {CATEGORIES.map((c) => by(c.k) > 0 && <Row key={c.k} muted label={c.label} bar={{ pct: (by(c.k) / (totals.total || 1)) * 100, color: "#9db4f5" }} value={eurShort(by(c.k))} />)}
          </div>
        </Card>
        <Card title="Needs your attention" icon={<AlertTriangle size={16} className="text-amber-600" />}>
          {flags.map((f, i) => <Flag key={i} pill={f.pill} ok={f.ok}>{f.text}</Flag>)}
        </Card>
      </div>
    </div>
  );
}
